

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import TecProductListCompare from "@tecumseh/Widgets/productlist/TecProductListCompare";
import TecProductListImage from "@tecumseh/Widgets/productlist/TecProductListImage";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    specialAlert?: InjectableCss;
    productInfo?: InjectableCss;
    productNumberText?: InjectableCss;
    productCardBorder?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 10px 0;
            min-width: 320px; /* prevent overlap with second column */
            div[class^="GridWrapper"] {
                div[class^="GridOffset"] {
                    width: 100% !important;
                }
            }
        `,
    },
    leftColumnGridItem: {
        width: [5, 4, 4, 3, 2],
        css: css`
            padding-right: 20px;
            flex-direction: column;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,

                    null,
                    null,
                    null,
                ])}
        `,
    },
    rightColumnGridItem: {
        width: [12, 8, 8, 9, 10],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 5px !important;
                    `,
                    css`
                        margin-left: 5px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            padding: 0px 15px 15px 15px;
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-left: 13px !important;
            padding: 2px;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    productInfo: {
        css: css`
            position: relative;
            padding-left: 13px !important;
            margin-top: 10px;
            padding: 0px;
            opacity: 1;
            color: rgba(0, 0, 0, 1);
            font-family: Poppins;
            font-size: 15px;
            font-weight: 600;
            font-style: normal;
            letter-spacing: 1px;
            text-align: left;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 18px;
                        &::after {
                            content: "";
                            position: absolute;
                            top: 23px;
                            left: 13px;
                            bottom: 0px;
                            height: 1.5px;
                            width: 92%;
                            background: rgb(0, 0, 0);
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    }
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <GridItem {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
            </GridItem>
            <GridItem {...styles.rightColumnGridItem}>
                <GridContainer {...styles.rightColumnGridContainer}>
                    <Clickable href={props.productInfo.productDetailPath} style={{display: 'flex', flexDirection: 'column'}} data-test-selector="productNumber">
                        <Typography data-test-selector="productNumber" {...styles.productInfo}>
                            {props.product.productNumber}
                        </Typography>
                    </Clickable>
                    <GridItem {...styles.infoGridItem}>
                        <ProductListProductInformation
                            showBrand={props.showBrand}
                            showTitle={props.showTitle}
                            showPartNumbers={props.showPartNumbers}
                            showAvailability={props.showAvailability}
                            showAttributes={props.showAttributes}
                            showImageBadges={props.showImageBadges}
                            maxImageBadges={props.maximumNumberOfImageBadges}
                            showTextBadges={props.showTextBadges}
                            maxTextBadges={props.maximumNumberOfTextBadges}
                        />
                    </GridItem>
                    <GridItem {...styles.actionsGridItem}>
                        <TecProductListCompare showCompare={props.showCompare}></TecProductListCompare>
                    </GridItem>
                    {/* <GridItem {...styles.searchScoreDataGridItem}>
                        <ProductSearchScoreData />
                    </GridItem> */}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default withProduct(ProductListProductCard);